import {
  ADMIN_TUTORIAL_AUTHORITIES,
  DEFAULT_TUTORIAL_PROGRESS,
  MODULE_FEATURE_KEYS,
  TUTORIAL_ROUTES,
  TUTORIAL_PROGRESS_VERSION,
  UI_TOUR_VERSION,
} from "./constants";

const safeObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const routeMatches = (pathname, prefixes = []) =>
  prefixes.some((prefix) => pathname.startsWith(prefix));

export const getNormalizedTutorialProgress = (incomingProgress) => {
  const progress = safeObject(incomingProgress);
  const coreSteps = safeObject(progress.coreSteps);
  const contextual = safeObject(progress.contextual);

  return {
    ...DEFAULT_TUTORIAL_PROGRESS,
    ...progress,
    version:
      typeof progress.version === "number"
        ? progress.version
        : TUTORIAL_PROGRESS_VERSION,
    uiTourVersion:
      typeof progress.uiTourVersion === "number"
        ? progress.uiTourVersion
        : UI_TOUR_VERSION,
    uiTourCompleted: Boolean(progress.uiTourCompleted),
    coreAdminOnboardingCompleted: Boolean(progress.coreAdminOnboardingCompleted),
    coreSteps: {
      users: Boolean(coreSteps.users),
      clients: Boolean(coreSteps.clients),
      jobs: Boolean(coreSteps.jobs),
    },
    contextual: {
      invoices: Boolean(contextual.invoices),
      manifests: Boolean(contextual.manifests),
      payouts: Boolean(contextual.payouts),
    },
  };
};

export const isAdminTutorialEligible = (user) => {
  if (!user) return false;
  if (Boolean(user.accountHolder)) return true;
  return ADMIN_TUTORIAL_AUTHORITIES.includes(user.authority);
};

export const isAccountHolderLike = (user) =>
  Boolean(user?.accountHolder) ||
  user?.authority === "owner" ||
  user?.authority === "superAdmin";

const shouldIncludeUserCreationStep = (user) =>
  Boolean(user?.accountHolder) ||
  ["owner", "admin", "superAdmin"].includes(user?.authority);

export const getCoreStepsForUser = (user, appType) => {
  const steps = [
    {
      id: "clients",
      title: "Create your first client",
      description: "Clients are required before creating jobs and invoices.",
      ctaLabel: "Create Client",
      createEntity: "client",
    },
    {
      id: "jobs",
      title: "Create your first job",
      description: "Use Jobs to start scheduling work and tracking operations.",
      ctaLabel: "Create Job",
      createEntity: "job",
    },
  ];

  if (shouldIncludeUserCreationStep(user)) {
    steps.unshift({
      id: "users",
      title:
        appType === "serviceRepair"
          ? "Create your first employee/user"
          : "Create your first user",
      description:
        "Add admins, dispatchers, drivers, technicians, or field users so work can be assigned.",
      ctaLabel:
        appType === "serviceRepair" ? "Create Employee/User" : "Create User",
      createEntity: "user",
    });
  }

  return steps;
};

export const maybeMarkCoreStepFromPath = (pathname, currentProgress) => {
  const progress = getNormalizedTutorialProgress(currentProgress);
  const nextCoreSteps = { ...progress.coreSteps };
  let changed = false;

  if (!nextCoreSteps.users && routeMatches(pathname, TUTORIAL_ROUTES.coreUsers)) {
    nextCoreSteps.users = true;
    changed = true;
  }

  if (
    !nextCoreSteps.clients &&
    routeMatches(pathname, TUTORIAL_ROUTES.coreClients)
  ) {
    nextCoreSteps.clients = true;
    changed = true;
  }

  if (!nextCoreSteps.jobs && routeMatches(pathname, TUTORIAL_ROUTES.coreJobs)) {
    nextCoreSteps.jobs = true;
    changed = true;
  }

  if (!changed) {
    return null;
  }

  const coreCompleted =
    nextCoreSteps.users && nextCoreSteps.clients && nextCoreSteps.jobs;

  return {
    ...progress,
    coreSteps: nextCoreSteps,
    coreAdminOnboardingCompleted: coreCompleted,
    completedAt: coreCompleted ? new Date().toISOString() : progress.completedAt,
    updatedAt: new Date().toISOString(),
  };
};

export const getContextualModuleFromPath = (pathname) => {
  if (routeMatches(pathname, TUTORIAL_ROUTES.invoices)) return "invoices";
  if (routeMatches(pathname, TUTORIAL_ROUTES.manifests)) return "manifests";
  if (routeMatches(pathname, TUTORIAL_ROUTES.payouts)) return "payouts";
  return null;
};

export const isModuleTutorialEligible = ({
  module,
  appType,
  featureFlags = {},
  progress,
}) => {
  if (!module) return false;

  // Manifests are trucking only.
  if (module === "manifests" && appType !== "trucking") {
    return false;
  }

  const featureKey = MODULE_FEATURE_KEYS[module];
  const featureEnabled =
    featureKey && featureFlags[featureKey] === undefined
      ? true
      : Boolean(featureFlags[featureKey]);

  if (!featureEnabled) {
    return false;
  }

  const normalized = getNormalizedTutorialProgress(progress);
  return !normalized.contextual[module];
};

export const getModuleCopy = (module) => {
  if (module === "invoices") {
    return {
      title: "Invoices walkthrough",
      body: "Create and send your first invoice from this section. You can also track paid and overdue balances here.",
      ctaPath: "/invoices/create",
      ctaLabel: "Create Invoice",
    };
  }

  if (module === "manifests") {
    return {
      title: "Manifests walkthrough",
      body: "Build manifests to group job legs, assign drivers, and keep trucking operations organized.",
      ctaPath: "/manifests",
      ctaLabel: "Open Manifests",
    };
  }

  return {
    title: "Payouts walkthrough",
    body: "Use payouts to manage driver/vendor payouts and keep payment records tied to work completed.",
    ctaPath: "/payouts/create",
    ctaLabel: "Create Payout",
  };
};

export const getUiTourSteps = (appType) => [
  {
    id: "sidebar",
    selector: '[data-tour="sidebar-nav"], [data-tour="sidebar-nav-mobile"]',
    title: "Navigation",
    description:
      "Use the sidebar to move between dashboard, jobs, clients, payouts, and settings.",
  },
  {
    id: "quickCreate",
    selector: '[data-tour="create-button"]',
    title: "Quick Create",
    description:
      "Create jobs, users, clients, invoices, manifests, and payouts from one place.",
  },
  {
    id: "notifications",
    selector: '[data-tour="notifications"]',
    title: "Notifications",
    description:
      "Keep up with real-time updates, reminders, and operational alerts here.",
  },
  {
    id: "accountMenu",
    selector: '[data-tour="account-menu"]',
    title: "Account Menu",
    description:
      "Open account settings, feedback, and onboarding shortcuts from this menu.",
  },
].filter((step) => {
  // Keep steps dynamic by app type if needed later.
  if (step.id === "sidebar" && !appType) return false;
  return true;
});
